import { generateHTML, type JSONContent } from '@tiptap/core';
import {
  NodeViewContent,
  NodeViewWrapper,
  type NodeViewProps,
  type ReactNodeViewProps,
} from '@tiptap/react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  CircleHelp,
  FileText,
  Flame,
  Info,
  Lightbulb,
  ListTree,
  Paperclip,
  Pencil,
  Quote,
  XCircle,
} from 'lucide-react';
import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { isAttachmentTarget } from '../extract';
import { schemaExtensions } from '../schema';
import { useHost } from './host';

// ---- [[wikilink]] ---------------------------------------------------------------

export function WikiLinkView({ node }: NodeViewProps) {
  const host = useHost();
  const { pageId, target, heading, alias } = node.attrs as {
    pageId: string | null;
    target: string;
    heading: string | null;
    alias: string | null;
  };
  const page = host.usePage(pageId);
  const title = page?.title ?? target;
  const label = alias ?? (heading ? `${title} › ${heading}` : title);
  const missing = !page && !host.findPageByName(target);

  const open = async (newTab: boolean) => {
    const found = page ?? host.findPageByName(target) ?? (await host.createPage(target));
    host.openPage(found.id, newTab);
  };

  return (
    <NodeViewWrapper
      as="span"
      className={`nx-wikilink${missing ? ' nx-wikilink-missing' : ''}`}
      data-page-id={pageId ?? undefined}
      title={missing ? host.t('editor.missingPage', { title: target }) : title}
      onClick={(e: React.MouseEvent) => {
        e.preventDefault();
        void open(e.ctrlKey || e.metaKey);
      }}
    >
      {label}
    </NodeViewWrapper>
  );
}

// ---- #tag -------------------------------------------------------------------------

export function HashtagView({ node }: NodeViewProps) {
  const host = useHost();
  const tag = node.attrs.tag as string;
  return (
    <NodeViewWrapper
      as="span"
      className="nx-tag"
      onClick={(e: React.MouseEvent) => {
        e.preventDefault();
        host.openTag(tag);
      }}
    >
      #{tag}
    </NodeViewWrapper>
  );
}

// ---- ![[embed]] ---------------------------------------------------------------------

/** An image that can be made smaller or larger by dragging its corner. */
function ResizableImage({
  src,
  alt,
  width,
  onResize,
}: {
  src: string;
  alt: string;
  width: number | null;
  onResize: (width: number | null) => void;
}) {
  const host = useHost();
  const [live, setLive] = useState<number | null>(null);
  const shown = live ?? width;
  return (
    <span className="nx-image" style={shown ? { width: shown } : undefined}>
      <img src={src} alt={alt} loading="lazy" draggable={false} />
      <span
        className="nx-image-resize"
        role="separator"
        aria-label={host.t('editor.resizeImage')}
        contentEditable={false}
        onPointerDown={(e) => {
          e.preventDefault();
          const img = (e.currentTarget.parentElement as HTMLElement).getBoundingClientRect();
          const max =
            (e.currentTarget.closest('.nx-prose') as HTMLElement | null)?.clientWidth ?? 2000;
          const startX = e.clientX;
          let next = img.width;
          const move = (ev: PointerEvent) => {
            next = Math.round(Math.min(max, Math.max(80, img.width + ev.clientX - startX)));
            setLive(next);
          };
          const up = () => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
            setLive(null);
            onResize(next >= max ? null : next);
          };
          window.addEventListener('pointermove', move);
          window.addEventListener('pointerup', up);
        }}
      />
    </span>
  );
}

function AttachmentEmbed({
  target,
  selected,
  width,
  updateAttributes,
}: {
  target: string;
  selected: boolean;
  width: number | null;
  updateAttributes: NodeViewProps['updateAttributes'];
}) {
  const host = useHost();
  const file = host.useAttachment(target);
  if (file?.mime.startsWith('image/')) {
    return (
      <NodeViewWrapper
        className={`nx-embed-image${selected ? ' is-selected' : ''}`}
        data-drag-handle
      >
        <ResizableImage
          src={file.url}
          alt={target}
          width={width}
          onResize={(w) => updateAttributes({ width: w })}
        />
      </NodeViewWrapper>
    );
  }
  return (
    <NodeViewWrapper
      className={`nx-embed nx-embed-file${selected ? ' is-selected' : ''}`}
      data-drag-handle
    >
      <Paperclip size={16} />
      <span>{target}</span>
      {file === null ? (
        <span className="nx-muted">{host.t('editor.attachmentMissing')}</span>
      ) : (
        file && (
          <button
            type="button"
            className="nx-link-button"
            onClick={() => host.openAttachment(target)}
          >
            {host.t('editor.openAttachment')}
          </button>
        )
      )}
    </NodeViewWrapper>
  );
}

/** Keep only the section under `heading` (until the next heading of the same or higher level). */
function section(doc: JSONContent, heading: string | null): JSONContent {
  if (!heading || !doc.content) return doc;
  const text = (n: JSONContent): string => n.text ?? (n.content ?? []).map(text).join('');
  const start = doc.content.findIndex(
    (n) => n.type === 'heading' && text(n).trim().toLowerCase() === heading.trim().toLowerCase(),
  );
  if (start === -1) return doc;
  const level = doc.content[start]!.attrs?.level as number;
  let end = start + 1;
  while (end < doc.content.length) {
    const n = doc.content[end]!;
    if (n.type === 'heading' && (n.attrs?.level as number) <= level) break;
    end++;
  }
  return { ...doc, content: doc.content.slice(start, end) };
}

export function EmbedView({ node, selected, updateAttributes }: NodeViewProps) {
  const host = useHost();
  const { pageId, target, heading } = node.attrs as {
    pageId: string | null;
    target: string;
    heading: string | null;
  };
  const resolved = host.usePage(pageId) ?? (pageId ? null : host.findPageByName(target));
  const content = host.useEmbedContent(resolved?.id ?? null);
  const html = useMemo(() => {
    if (!content) return '';
    try {
      return generateHTML(section(content, heading), schemaExtensions());
    } catch {
      return '';
    }
  }, [content, heading]);

  if (isAttachmentTarget(target)) {
    return (
      <AttachmentEmbed
        target={target}
        selected={selected}
        width={(node.attrs.width as number | null) ?? null}
        updateAttributes={updateAttributes}
      />
    );
  }

  return (
    <NodeViewWrapper className={`nx-embed${selected ? ' is-selected' : ''}`} data-drag-handle>
      <button
        type="button"
        className="nx-embed-header"
        onClick={(e) => resolved && host.openPage(resolved.id, e.ctrlKey || e.metaKey)}
      >
        <FileText size={14} />
        <span>{resolved?.title ?? target}</span>
        {heading && <span className="nx-muted">› {heading}</span>}
      </button>
      {!resolved ? (
        <div className="nx-muted nx-embed-body">
          {host.t('editor.missingPage', { title: target })}
        </div>
      ) : content === undefined ? null : (
        <div className="nx-embed-body nx-prose" dangerouslySetInnerHTML={{ __html: html }} />
      )}
    </NodeViewWrapper>
  );
}

// ---- ![](image) -----------------------------------------------------------------------

const isUrl = (src: string) => /^(https?:|data:|blob:)/i.test(src);

/** Markdown images; a relative path (`Bijlagen/foto.png`) is looked up among the attachments. */
export function ImageView({ node }: NodeViewProps) {
  const host = useHost();
  const src = (node.attrs.src as string | null) ?? '';
  const name = isUrl(src) ? null : decodeURIComponent(src.split(/[\\/]/).pop() ?? '');
  const file = host.useAttachment(name);
  const url = name ? file?.url : src;
  return (
    <NodeViewWrapper as="span" className="nx-inline-image">
      {url ? (
        <img src={url} alt={(node.attrs.alt as string | null) ?? ''} loading="lazy" />
      ) : (
        <span className="nx-muted">{name ?? src}</span>
      )}
    </NodeViewWrapper>
  );
}

// ---- callout ---------------------------------------------------------------------------

const CALLOUT_ICONS: Record<string, ReactNode> = {
  note: <Pencil size={16} />,
  info: <Info size={16} />,
  tip: <Flame size={16} />,
  hint: <Lightbulb size={16} />,
  important: <Flame size={16} />,
  success: <CheckCircle2 size={16} />,
  check: <CheckCircle2 size={16} />,
  done: <CheckCircle2 size={16} />,
  question: <CircleHelp size={16} />,
  help: <CircleHelp size={16} />,
  faq: <CircleHelp size={16} />,
  warning: <AlertTriangle size={16} />,
  caution: <AlertTriangle size={16} />,
  attention: <AlertTriangle size={16} />,
  failure: <XCircle size={16} />,
  fail: <XCircle size={16} />,
  missing: <XCircle size={16} />,
  danger: <XCircle size={16} />,
  error: <XCircle size={16} />,
  bug: <XCircle size={16} />,
  example: <ListTree size={16} />,
  quote: <Quote size={16} />,
  cite: <Quote size={16} />,
  abstract: <ListTree size={16} />,
  summary: <ListTree size={16} />,
  tldr: <ListTree size={16} />,
  todo: <CheckCircle2 size={16} />,
};

/** Colour family per callout kind (Obsidian's grouping). */
export function calloutTone(kind: string): string {
  const k = kind.toLowerCase();
  if (['tip', 'hint', 'important', 'abstract', 'summary', 'tldr'].includes(k)) return 'teal';
  if (['success', 'check', 'done'].includes(k)) return 'green';
  if (['question', 'help', 'faq', 'warning', 'caution', 'attention'].includes(k)) return 'orange';
  if (['failure', 'fail', 'missing', 'danger', 'error', 'bug'].includes(k)) return 'red';
  if (['example'].includes(k)) return 'purple';
  if (['quote', 'cite'].includes(k)) return 'gray';
  return 'blue';
}

/** `> [!toggle]- Title`: a Notion-style toggle (in Obsidian a foldable callout). */
function ToggleView({ node, updateAttributes }: NodeViewProps) {
  const host = useHost();
  const { title, fold } = node.attrs as { title: string; fold: string | null };
  const open = fold !== '-';
  return (
    <NodeViewWrapper className="nx-toggle" data-open={open}>
      <div className="nx-toggle-header" contentEditable={false}>
        <button
          type="button"
          className="nx-icon-button"
          aria-expanded={open}
          aria-label={host.t(open ? 'editor.collapse' : 'editor.expand')}
          onClick={() => updateAttributes({ fold: open ? '-' : '+' })}
        >
          <ChevronDown size={16} style={{ transform: open ? undefined : 'rotate(-90deg)' }} />
        </button>
        <input
          className="nx-callout-title"
          value={title}
          placeholder={host.t('editor.toggleTitle')}
          aria-label={host.t('editor.toggleTitle')}
          onChange={(e) => updateAttributes({ title: e.target.value })}
        />
      </div>
      <NodeViewContent className="nx-toggle-body" hidden={!open} />
    </NodeViewWrapper>
  );
}

export function CalloutView(props: NodeViewProps) {
  const { node, updateAttributes } = props;
  const host = useHost();
  const { kind, title, fold } = node.attrs as { kind: string; title: string; fold: string | null };
  const [open, setOpen] = useState(fold !== '-');
  const k = kind.toLowerCase();
  if (k === 'toggle') return <ToggleView {...props} />;
  return (
    <NodeViewWrapper className="nx-callout" data-tone={calloutTone(k)}>
      <div className="nx-callout-header" contentEditable={false}>
        <span className="nx-callout-icon">{CALLOUT_ICONS[k] ?? <Info size={16} />}</span>
        <input
          className="nx-callout-title"
          value={title}
          placeholder={k.charAt(0).toUpperCase() + k.slice(1)}
          aria-label={host.t('editor.calloutTitle')}
          onChange={(e) => updateAttributes({ title: e.target.value })}
        />
        {fold !== null && (
          <button
            type="button"
            className="nx-icon-button"
            aria-expanded={open}
            aria-label={host.t(open ? 'editor.collapse' : 'editor.expand')}
            onClick={() => setOpen(!open)}
          >
            <ChevronDown size={16} style={{ transform: open ? undefined : 'rotate(-90deg)' }} />
          </button>
        )}
      </div>
      <NodeViewContent className="nx-callout-body" hidden={!open} />
    </NodeViewWrapper>
  );
}

// ---- raw (preserved) markdown -------------------------------------------------------------

export function RawBlockView({ node, updateAttributes, selected }: NodeViewProps) {
  const host = useHost();
  const text = node.attrs.text as string;
  return (
    <NodeViewWrapper className={`nx-raw${selected ? ' is-selected' : ''}`} data-drag-handle>
      <div className="nx-raw-label" contentEditable={false}>
        {host.t('editor.rawBlock')}
      </div>
      <textarea
        className="nx-raw-text"
        value={text}
        spellCheck={false}
        rows={Math.min(12, text.split('\n').length)}
        aria-label={host.t('editor.rawBlock')}
        onChange={(e) => updateAttributes({ text: e.target.value })}
      />
    </NodeViewWrapper>
  );
}

// ---- code blocks (+ mermaid preview) -------------------------------------------------------

const LANGUAGES = [
  '',
  'bash',
  'powershell',
  'javascript',
  'typescript',
  'jsx',
  'tsx',
  'json',
  'yaml',
  'python',
  'sql',
  'html',
  'css',
  'markdown',
  'xml',
  'ini',
  'dataview',
  'tasks',
  'mermaid',
  'text',
];

function MermaidPreview({ code }: { code: string }) {
  const host = useHost();
  const id = useId().replace(/[^a-zA-Z0-9]/g, '');
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const mermaid = (await import('mermaid')).default;
        const dark = document.documentElement.dataset.theme === 'dark';
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: 'strict',
          theme: dark ? 'dark' : 'default',
        });
        const out = await mermaid.render(`nx-mermaid-${id}`, code);
        if (!cancelled) {
          setSvg(out.svg);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [code, id]);
  if (error) return <div className="nx-mermaid-error">{host.t('editor.mermaidError')}</div>;
  return <div className="nx-mermaid" dangerouslySetInnerHTML={{ __html: svg }} />;
}

export function CodeBlockView({ node, updateAttributes }: ReactNodeViewProps) {
  const host = useHost();
  const language = (node.attrs.language as string | null) ?? '';
  const known = LANGUAGES.includes(language);
  // A tasks query shows its results; the query text itself only on request.
  const TaskQuery = language === 'tasks' ? host.TaskQuery : undefined;
  const [showQuery, setShowQuery] = useState(false);
  const hideCode = !!TaskQuery && !showQuery;
  return (
    <NodeViewWrapper className={`nx-codeblock${hideCode ? ' nx-codeblock-collapsed' : ''}`}>
      <div className="nx-codeblock-bar" contentEditable={false}>
        {TaskQuery && (
          <button
            type="button"
            className="nx-codeblock-toggle"
            aria-expanded={showQuery}
            onClick={() => setShowQuery((v) => !v)}
          >
            {host.t(showQuery ? 'editor.hideQuery' : 'editor.editQuery')}
          </button>
        )}
        <select
          value={language}
          aria-label={host.t('editor.codeLanguage')}
          onChange={(e) => updateAttributes({ language: e.target.value || null })}
        >
          {!known && <option value={language}>{language}</option>}
          {LANGUAGES.map((l) => (
            <option key={l} value={l}>
              {l || host.t('editor.plainText')}
            </option>
          ))}
        </select>
      </div>
      <pre>
        <NodeViewContent<'code'>
          as="code"
          className={language ? `language-${language}` : undefined}
        />
      </pre>
      {language === 'mermaid' && <MermaidPreview code={node.textContent} />}
      {TaskQuery && <TaskQuery query={node.textContent} />}
    </NodeViewWrapper>
  );
}
